import { useAuth } from '@clerk/clerk-react';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { z } from 'zod';
import { workflowsApi } from '~/api/def/servers/defaultServer';
import {
  TaskName,
  TaskState,
  Workflow,
  workflowSchema,
} from '~/atoms/schemas/workflowSchemas';
import { WorkflowsState, workflowsAtom } from '~/atoms/workflowsAtoms';

import {
  SSEConnection,
  SSEConnectionConfig,
  createSSEConnection,
  subscribe,
} from './sseConnection';

export type { Workflow };

const mergeWorkflows = (
  prev: WorkflowsState,
  workflow: Workflow,
  pageSize?: number,
): WorkflowsState => {
  const workflowsMap = new Map(prev.workflowsMap);
  const exists = workflowsMap.has(workflow.workflow_id);

  workflowsMap.set(workflow.workflow_id, workflow);

  let updatedWorkflows: Workflow[];

  if (exists) {
    updatedWorkflows = prev.workflows.map((w) =>
      w.workflow_id === workflow.workflow_id ? workflow : w,
    );
  } else {
    updatedWorkflows = [workflow, ...prev.workflows];
  }

  // Sort workflows by last_modified_at in descending order (newest first)
  updatedWorkflows.sort(
    (a, b) =>
      new Date(b.last_modified_at as string).getTime() -
      new Date(a.last_modified_at as string).getTime(),
  );

  // If pageSize is specified, limit the number of workflows
  if (pageSize && updatedWorkflows.length > pageSize) {
    // Clean up workflowsMap to only contain the workflows we're keeping
    const newWorkflowsMap = new Map();
    updatedWorkflows.forEach((w) => {
      newWorkflowsMap.set(w.workflow_id, w);
    });
    // Keep only the most recent workflows up to pageSize
    const slicedEntries = Array.from(newWorkflowsMap.entries()).slice(
      0,
      pageSize,
    );
    const limitedWorkflowsMap = new Map(slicedEntries);
    // Update both the map and array to use the limited set
    return {
      ...prev,
      workflows: Array.from(limitedWorkflowsMap.values()),
      workflowsMap: limitedWorkflowsMap,
    };
  }

  return {
    ...prev,
    workflows: updatedWorkflows,
    workflowsMap,
  };
};

// Workflow Connection setup
export const useWorkflowConnection = ({
  workflowId,
  page,
  pageSize,
}: {
  workflowId?: string;
  page: number;
  pageSize?: number;
}) => {
  const { getToken } = useAuth();
  const [workflows, setWorkflows] = useAtom(workflowsAtom);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const isConnectedRef = useRef(false);

  useEffect(() => {
    // Reset connection state when parameters change
    isConnectedRef.current = false;
    setWorkflows((prev) => ({
      ...prev,
      workflows: [],
      workflowsMap: new Map(),
      isLoading: true,
      error: null,
    }));

    const workflowConnection: SSEConnection<Workflow, Workflow> =
      createSSEConnection<Workflow, Workflow>();

    const config: SSEConnectionConfig<Workflow, Workflow> = {
      url: workflowId
        ? `${workflowsApi.url}/workflows/${workflowId}`
        : `${workflowsApi.url}/workflows?format=full&page=${page}&page_size=${pageSize}`,
      schema: workflowSchema as z.ZodType<Workflow>,
      dataConverter: (data) => data,
    };

    unsubscribeRef.current = subscribe<Workflow, Workflow>(
      workflowConnection,
      config,
      (workflow) => {
        isConnectedRef.current = true;
        setWorkflows((prev) => mergeWorkflows(prev, workflow, pageSize));
      },
      (error) => {
        isConnectedRef.current = false;
        setWorkflows((prev) => ({ ...prev, error }));
      },
      (loading) => {
        setWorkflows((prev) => ({ ...prev, isLoading: loading }));
      },
      getToken,
      workflowId ? `Workflow-${workflowId}` : 'Workflows',
    );

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      isConnectedRef.current = false;
    };
  }, [getToken, workflowId, setWorkflows, page, pageSize]);

  return {
    workflows,
    setWorkflows,
    isConnected: isConnectedRef.current,
  };
};

export const useWorkflows = ({
  workflowId,
}: {
  workflowId?: string;
} = {}) => {
  const [workflows] = useAtom(workflowsAtom);

  // Create a connection if one isn't running
  const { isConnected } = useWorkflowConnection({
    workflowId,
    page: 0,
    pageSize: 10, // Default page size
  });

  const activeCount = useMemo(() => {
    return workflows.workflows.filter(
      (w) => w.status === 'running' || w.status === 'starting',
    ).length;
  }, [workflows.workflows]);

  const getWorkflowById = useCallback(
    (id: string) => {
      return workflows.workflowsMap.get(id) || null;
    },
    [workflows.workflowsMap],
  );

  const selectedWorkflow = useMemo(
    () => (workflowId ? getWorkflowById(workflowId) : null),
    [workflowId, getWorkflowById],
  );

  const memoWorkflows = useMemo(() => {
    if (selectedWorkflow) {
      // Filter out the selected workflow from the workflows array to prevent duplicates
      return workflows.workflows
        .filter((w) => w.workflow_id !== selectedWorkflow.workflow_id)
        .concat([selectedWorkflow]);
    }
    return workflows.workflows;
  }, [selectedWorkflow, workflows.workflows]);

  return {
    workflows: memoWorkflows,
    selectedWorkflow,
    isLoading: workflows.isLoading,
    error: workflows.error,
    activeCount,
    getWorkflowById,
    isConnected,
  };
};

export type TaskEntry = {
  taskName: TaskName;
  taskState: TaskState;
  workflowId: string;
  startedAt: string;
};

export const getTasksFromWorkflow = (workflow: Workflow): TaskEntry[] => {
  if (!workflow || !workflow.task_states) return [];

  return Object.entries(workflow.task_states).map(([taskName, taskState]) => ({
    taskName: taskName as TaskName,
    taskState,
    workflowId: workflow.workflow_id,
    startedAt: taskState.created_at,
  }));
};

export const getAllTasksFromWorkflows = (
  workflows: Workflow[],
): TaskEntry[] => {
  return workflows.flatMap((workflow) => getTasksFromWorkflow(workflow));
};
